import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { DataTable } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import moment from "moment";
import { GET_TRANSACTIONS } from "graphql/transaction";
import { formatCashFull } from "utils/formatCash";
import { DEFAULT_PAGINATION_SIZE } from "components/common/Constants";
import RenderMerchantDisplay from "components/common/RenderMerchantDisplay";
import { RenderTransactionTypeChip } from "components/common/RenderTransactionType";
import { RenderTransactionStatus } from "components/common/RenderTransactionStatus";
import { RenderTransactionDirection } from "components/common/RenderTransactionDirection";
import type { TransactionDirection } from "components/common/RenderTransactionDirection";
import TransactionsType from "enums/TransactionsType";
import TransactionStatus from "enums/TransactionStatus";
import WorkflowRequestStatus from "enums/WorkflowRequestStatus";
import useInfiniteScroll from "hooks/useInfiniteScroll";

/**
 * No transaction carries a direction, so it is derived. The type wins where it
 * states one: every INBOUND_ and OUTBOUND_ member of the enum names its own
 * direction. Everything else (a card capture, a refund, a reimbursement) falls
 * to the sign, which `useCashFlowSummary` documents as the server's convention:
 * a positive amount is money arriving.
 */
export const directionOf = (
  type: TransactionsType,
  amount: number,
): TransactionDirection => {
  if (type?.startsWith("INBOUND_")) return "DEPOSIT";
  if (type?.startsWith("OUTBOUND_")) return "WITHDRAWAL";
  if (amount > 0) return "DEPOSIT";
  if (amount < 0) return "WITHDRAWAL";
  return "UNKNOWN";
};

type Row = {
  id: string;
  date: Date;
  merchant: string;
  url?: string;
  logo?: string;
  cachedLogo?: string;
  type: TransactionsType;
  cardType?: string;
  amount: string;
  /** Signed figure behind `amount`: the sign is what colours the cell. */
  amountValue: number;
  direction: TransactionDirection;
  status: string;
  disputeStatus?: string;
  /** RenderTransactionStatus overrides the ledger status from this. */
  workflowStatus?: WorkflowRequestStatus;
};

/**
 * Only the fields this table reads. The query is shared, so the node carries
 * more than this; typing what is used beats an `any`.
 */
interface TransactionNode {
  id: string;
  merchant?: string | null;
  type?: string | null;
  status?: string | null;
  disputeStatus?: string | null;
  workflowRequest?: { status?: WorkflowRequestStatus | null } | null;
  amount: number;
  currency?: string | null;
  transactionDate: string | number;
  merchantData?: {
    url?: string | null;
    logo?: string | null;
    cachedLogo?: string | null;
  } | null;
  card?: { type?: string | null } | null;
}

type TransactionConnection = Record<
  string,
  { edges: { node: TransactionNode }[] } | undefined
>;

const extractTransactions = (data: TransactionConnection): Row[] => {
  const firstKey = Object.keys(data)[0];
  return (data[firstKey]?.edges ?? []).map(({ node }) => {
    const type = node.type as TransactionsType;
    return {
      id: node.id,
      date: new Date(node.transactionDate),
      merchant: node.merchant ?? "",
      url: node.merchantData?.url ?? undefined,
      logo: node.merchantData?.logo ?? undefined,
      cachedLogo: node.merchantData?.cachedLogo ?? undefined,
      type,
      cardType: node.card?.type ?? undefined,
      amount: formatCashFull(node.amount, node.currency),
      amountValue: node.amount,
      direction: directionOf(type, node.amount),
      status: node.status ?? "",
      disputeStatus: node.disputeStatus ?? undefined,
      workflowStatus: node.workflowRequest?.status ?? undefined,
    };
  });
};

interface BankTransactionsTableProps {
  /**
   * Scopes every row to one account. There is no unscoped mode: without an id
   * the query is skipped, because an absent accountId reads as every account
   * server-side and this surface must never show one.
   */
  accountId?: string;
  searchTerm?: string;
  types: TransactionsType[];
  statuses: TransactionStatus[];
  refreshIndex?: number;
  /**
   * The account list is still resolving. Skipping the query makes Apollo
   * report loading=false, so without this the table shows "none found" before
   * it has been allowed to ask.
   */
  loading?: boolean;
  emptyState: string;
  onRowClick?: (transactionId: string) => void;
}

/**
 * The activity ledger for one bank account. A sibling of the Transactions
 * page's table rather than a variant of it: this one is six columns in the
 * bank's own order, with a Direction the shared table does not carry, and
 * giving that table a column-selection prop would spread this page's layout
 * across a component four other pages depend on.
 */
const BankTransactionsTable: React.FC<BankTransactionsTableProps> = ({
  accountId,
  searchTerm,
  types,
  statuses,
  refreshIndex,
  loading = false,
  emptyState,
  onRowClick,
}) => {
  const { t } = useTranslation();

  // Memoised rather than rebuilt: `useInfiniteScroll` stringifies this to
  // decide when to reset, so a fresh object every render refetches forever.
  const initialVariables = useMemo(
    () => ({
      first: DEFAULT_PAGINATION_SIZE,
      after: undefined,
      accountId: accountId || null,
      searchTerm,
      type: types,
      transactionStatus: statuses,
    }),
    [accountId, searchTerm, types, statuses],
  );

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_TRANSACTIONS,
      initialVariables,
      extractData: extractTransactions,
      refetchIndex: refreshIndex,
      // An absent accountId reads as every account, so an unresolved selection
      // would list the whole company for a frame.
      skip: !accountId,
    });

  const columns: DataColumn<Row>[] = useMemo(
    () => [
      {
        id: "date",
        header: t("Date"),
        cell: (row) => moment(row.date).format("MM/DD/YYYY"),
      },
      {
        id: "counterparty",
        header: t("Counterparty"),
        maxWidth: 200,
        cell: (row) => (
          <RenderMerchantDisplay
            merchant={row.merchant}
            url={row.url}
            logo={row.logo}
            cachedLogo={row.cachedLogo}
          />
        ),
      },
      {
        id: "type",
        header: t("Type"),
        width: 0,
        cell: (row) => (
          <RenderTransactionTypeChip
            transactionType={row.type}
            cardType={row.cardType}
          />
        ),
      },
      {
        id: "amount",
        header: t("Amount"),
        cell: (row) => ({
          content: (
            <Typography
              variant="body1"
              sx={{
                color:
                  row.amountValue > 0 ? "var(--color-positive)" : undefined,
                fontWeight: "var(--type-weight-semibold)",
              }}
            >
              {row.amount}
            </Typography>
          ),
          align: "right" as const,
        }),
      },
      {
        id: "direction",
        header: t("Direction"),
        width: 0,
        cell: (row) => <RenderTransactionDirection direction={row.direction} />,
      },
      {
        id: "status",
        header: t("Status"),
        width: 0,
        cell: (row) => (
          <RenderTransactionStatus
            transactionStatus={row.status}
            disputeStatus={row.disputeStatus}
            workflowStatus={row.workflowStatus}
            transactionType={row.type}
          />
        ),
      },
    ],
    [t],
  );

  return (
    <Box sx={{ position: "relative", height: "100%" }}>
      <DataTable<Row>
        columns={columns}
        rows={dataArray}
        getRowKey={(row) => row.id}
        loading={loading || displayLoading}
        isFetchingMore={isInfiniteScrollFetching}
        onRowClick={onRowClick ? (row) => onRowClick(row.id) : undefined}
        onScroll={handleScroll}
        emptyState={emptyState}
        loadingMoreText={t("LoadingMore")}
        stickyHeader
      />
    </Box>
  );
};

export default BankTransactionsTable;
